"use client"

import { reatomComponent } from "@reatom/react"
import { useEffect, useRef, useState } from "react"
import {
  pauseGachaRevealWhoosh,
  playGachaRevealWhooshAt,
  syncGachaRevealWhooshToPlayback,
} from "./gacha-reveal-audio"
import { GACHA_REVEAL_FADE_MS } from "./gacha-reveal-handoff"
import type { WatchRevealPlan } from "./gacha-watch-reveal"

type GachaWatchMultiRevealProps = {
  plan: Extract<WatchRevealPlan, { kind: "watch-multi" }>
  muted: boolean
  onFade: () => void
  onComplete: () => void
}

export const GachaWatchMultiReveal =
  reatomComponent<GachaWatchMultiRevealProps>(
    ({ muted, onComplete, onFade, plan }) => {
      const [showFlash, setShowFlash] = useState(false)
      const boxVideoRef = useRef<HTMLVideoElement>(null)
      const playbackSyncRef = useRef<() => void>(() => undefined)
      const boxPlayingRef = useRef(false)
      const flashVisibleRef = useRef(false)
      const handoffStartedRef = useRef(false)
      const mutedRef = useRef(muted)
      const onFadeRef = useRef(onFade)
      const onCompleteRef = useRef(onComplete)
      const completeRef = useRef(false)

      useEffect(() => {
        onFadeRef.current = onFade
        onCompleteRef.current = onComplete
      }, [onComplete, onFade])

      useEffect(() => {
        mutedRef.current = muted
      }, [muted])

      useEffect(() => {
        let animationFrameId = 0
        let completeId = 0

        boxPlayingRef.current = false
        flashVisibleRef.current = false
        handoffStartedRef.current = false
        completeRef.current = false
        setShowFlash(false)
        pauseGachaRevealWhoosh()

        const startHandoff = () => {
          if (handoffStartedRef.current) return

          handoffStartedRef.current = true
          onFadeRef.current()
          completeId = window.setTimeout(() => {
            if (completeRef.current) return

            completeRef.current = true
            onCompleteRef.current()
          }, GACHA_REVEAL_FADE_MS)
        }

        const syncToBoxPlayback = () => {
          const boxVideo = boxVideoRef.current
          if (!boxVideo) return

          if (boxPlayingRef.current) {
            syncGachaRevealWhooshToPlayback(
              boxVideo.currentTime,
              mutedRef.current,
            )
          }

          const playbackMs = boxVideo.currentTime * 1000
          if (playbackMs >= plan.flashStartsAtMs && !flashVisibleRef.current) {
            flashVisibleRef.current = true
            setShowFlash(true)
          }

          if (playbackMs >= plan.handoffAtMs) {
            startHandoff()
          }
        }

        const syncAnimationFrame = () => {
          syncToBoxPlayback()
          animationFrameId = window.requestAnimationFrame(syncAnimationFrame)
        }

        playbackSyncRef.current = syncToBoxPlayback
        animationFrameId = window.requestAnimationFrame(syncAnimationFrame)

        return () => {
          playbackSyncRef.current = () => undefined
          window.cancelAnimationFrame(animationFrameId)
          window.clearTimeout(completeId)
          pauseGachaRevealWhoosh()
        }
      }, [plan.flashStartsAtMs, plan.handoffAtMs])

      return (
        <div
          className="relative h-full w-full overflow-hidden"
          data-testid="watch-multi-reveal"
        >
          <video
            ref={boxVideoRef}
            aria-label="Opened Watch box"
            autoPlay
            muted
            className="absolute inset-0 h-full w-full object-contain"
            playsInline
            preload="auto"
            src={plan.boxSrc}
            onEnded={() => {
              boxPlayingRef.current = false
              pauseGachaRevealWhoosh()
              playbackSyncRef.current()
            }}
            onPause={() => {
              boxPlayingRef.current = false
              pauseGachaRevealWhoosh()
            }}
            onPlaying={(event) => {
              boxPlayingRef.current = true
              playGachaRevealWhooshAt(
                event.currentTarget.currentTime,
                mutedRef.current,
              )
              playbackSyncRef.current()
            }}
            onTimeUpdate={() => playbackSyncRef.current()}
            onWaiting={() => {
              boxPlayingRef.current = false
              pauseGachaRevealWhoosh()
            }}
          />
          {showFlash ? (
            <div
              aria-label="Watch reveal flash"
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle, rgb(255 255 255) 0%, rgb(255 235 167 / 85%) 30%, rgb(255 205 83 / 25%) 58%, transparent 78%)",
              }}
            />
          ) : null}
        </div>
      )
    },
    "GachaWatchMultiReveal",
  )
